import { supabase } from '../lib/supabase';
import { parseInvoiceWithAI } from '../lib/gemini';
import { validateFile } from './fileValidation';

const toNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const roundMoney = (value) => {
  const n = toNumber(value, null);
  if (n === null) return null;
  return Math.round(n * 100) / 100;
};

const sanitizeParsedResult = (raw) => {
  if (!raw) return raw;
  const lines = Array.isArray(raw?.line_items) ? raw.line_items : [];
  const reportedTotals = {
    net: roundMoney(raw?.net_amount),
    vat: roundMoney(raw?.vat_amount),
    gross: roundMoney(raw?.gross_amount)
  };
  let sumNet = 0;
  let sumVat = 0;
  let sumGross = 0;
  const sanitizedLines = lines.map((line) => {
    const quantity = toNumber(line?.quantity, null);
    const unitPrice = toNumber(line?.unit_price, null);
    const vatRate = toNumber(line?.vat_rate, null);
    let net = toNumber(line?.net_amount, null);
    if (net === null && quantity !== null && unitPrice !== null) {
      net = roundMoney(quantity * unitPrice);
    }
    if (net === null) net = 0;
    let vat = toNumber(line?.vat_amount, null);
    if ((vat === null || (vat === 0 && vatRate)) && vatRate !== null) {
      vat = roundMoney(net * (vatRate / 100));
    }
    if (vat === null) vat = 0;
    let total = toNumber(line?.total, null);
    const expectedTotal = roundMoney(net + vat);
    if (total === null || (expectedTotal !== null && Math.abs(total - expectedTotal) > 0.01)) {
      total = expectedTotal;
    }
    sumNet += net || 0;
    sumVat += vat || 0;
    sumGross += total || 0;
    return {
      ...line,
      quantity: quantity ?? 0,
      unit_price: unitPrice ?? 0,
      net_amount: net ?? 0,
      vat_amount: vat ?? 0,
      vat_rate: vatRate ?? 0,
      total: total ?? (net ?? 0) + (vat ?? 0)
    };
  });

  const sanitized = {
    ...raw,
    line_items: sanitizedLines,
    line_item_sums: {
      net: roundMoney(sumNet),
      vat: roundMoney(sumVat),
      gross: roundMoney(sumGross)
    },
    reported_totals: reportedTotals
  };

  return sanitized;
};

/**
 * Validate upload file with comprehensive security checks
 * @param {File} file - File to validate
 * @returns {Promise<{sanitizedName: string}>} Validation result with sanitized filename
 * @throws {Error} If validation fails
 */
async function validateUploadFile(file) {
  const result = await validateFile(file);

  if (!result.valid) {
    throw new Error(result.errors.join(' '));
  }

  return { sanitizedName: result.sanitizedName };
}

/**
 * Invoice service for managing documents, parsing, and data operations
 */
export class InvoiceService {
  
  /**
   * Upload document to storage and create database record
   * @param {File} file - Document file to upload
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Document record
   */
  static async uploadDocument(file, userId, orgId) {
    try {
      // Validate file type, size, and magic bytes before upload
      const { sanitizedName } = await validateUploadFile(file);

      // Compute file hash (SHA-256) for deduplication
      const fileSize = typeof file.size === 'number' ? file.size : 0;
      const fileSha256 = await computeFileSha256(file);

      // Check for duplicate by org + sha256 (idempotent reprocessing support)
      const { data: existing, error: dupError } = await supabase
        ?.from('documents')
        ?.select('*')
        ?.eq('org_id', orgId)
        ?.eq('file_sha256', fileSha256)
        ?.limit(1);
      if (dupError) throw dupError;
      if (existing && existing.length > 0) {
        // Return the existing document instead of throwing — caller can reprocess
        return existing[0];
      }

      // Generate unique file path with sanitized filename
      const timestamp = Date.now();
      const fileName = `${timestamp}_${sanitizedName}`;
      const filePath = `${orgId}/invoices/${fileName}`;
      
      // Upload to Supabase storage
      const { error: uploadError } = await supabase?.storage?.from('invoice-documents')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Create document record with sanitized filename
      const { data: documentData, error: documentError } = await supabase?.from('documents')?.insert({
          user_id: userId,
          org_id: orgId,
          file_name: sanitizedName,
          file_ext: file?.name?.split('.')?.pop()?.toLowerCase(),
          storage_path: filePath,
          file_size: fileSize,
          file_sha256: fileSha256,
          hash_algo: 'sha256',
          source: 'upload',
          pages: 1, // Default, can be updated after processing
          is_multi_invoice: false
        })?.select()?.single();

      if (documentError) throw documentError;

      return documentData;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  /**
   * Parse invoice using AI and save results
   * @param {string} documentId - Document ID
   * @param {string} userId - User ID
   * @param {AbortSignal} signal - Abort signal for cancellation
   * @returns {Promise<Object>} Parsed invoice data
   */
  static async parseInvoice(documentId, userId, orgId, signal = null, options = {}) {
    try {
      const modelToUse = options?.model || 'gemini-2.0-flash';

      // Get document info
      const { data: document, error: docError } = await supabase?.from('documents')?.select('*')?.eq('id', documentId)?.eq('org_id', orgId)?.single();

      if (docError || !document) throw new Error('Document not found');

      // Get signed URL for file access
      const { data: signedUrlData, error: urlError } = await supabase?.storage?.from('invoice-documents')?.createSignedUrl(document?.storage_path, 3600);

      if (urlError) throw urlError;

      // Fetch file as blob
      const response = await fetch(signedUrlData?.signedUrl);
      if (!response?.ok) throw new Error('Failed to fetch document');
      
      const blob = await response?.blob();
      const file = new File([blob], document.file_name, { 
        type: blob.type || 'application/pdf' 
      });

      // Parse with AI
      const prompt = `Parse this invoice document and extract all relevant information. Pay special attention to vendor details, amounts, dates, and line items.`;
      const aiResult = await parseInvoiceWithAI(prompt, file, signal, modelToUse);

      const normalizedConfidence = typeof aiResult?.confidence === 'number'
        ? Math.max(0, Math.min(1, aiResult?.confidence))
        : 0.8;
      let finalConfidence = normalizedConfidence;
      let confidenceOverrideReason = null;

      const invoiceDateObj = aiResult?.invoice_date ? new Date(aiResult?.invoice_date) : null;
      const isValidInvoiceDate = invoiceDateObj instanceof Date && !Number.isNaN(invoiceDateObj?.getTime());

      if (!isValidInvoiceDate) {
        finalConfidence = 0.25;
        confidenceOverrideReason = 'date_invalid_or_missing';
      }

      const normalizedResult = {
        ...aiResult,
        confidence: finalConfidence,
      };

      if (confidenceOverrideReason) {
        normalizedResult.confidence_original = normalizedConfidence;
        normalizedResult.confidence_override_reason = confidenceOverrideReason;
        normalizedResult.confidence_capped_at = finalConfidence;
      }

      // Match vendor
      const sanitizedResult = sanitizeParsedResult(normalizedResult);

      const matchedVendor = await this.matchVendor(sanitizedResult?.vendor_name, orgId);
      const headerTotals = sanitizedResult?.reported_totals || {};
      const fallbackTotals = sanitizedResult?.line_item_sums || {};
      const trustedTotals = {
        net: roundMoney(headerTotals?.net ?? fallbackTotals?.net ?? sanitizedResult?.net_amount ?? 0),
        vat: roundMoney(headerTotals?.vat ?? fallbackTotals?.vat ?? sanitizedResult?.vat_amount ?? 0),
        gross: roundMoney(headerTotals?.gross ?? fallbackTotals?.gross ?? sanitizedResult?.gross_amount ?? 0)
      };

      // Prepare invoice data for insertion
  const invoicePayload = {
        document_id: documentId,
        user_id: userId,
        org_id: orgId,
        vendor_name: sanitizedResult?.vendor_name,
        invoice_number: sanitizedResult?.invoice_number,
        invoice_date: sanitizedResult?.invoice_date,
        due_date: sanitizedResult?.due_date,
        currency: sanitizedResult?.currency || 'USD',
        net_amount: trustedTotals.net,
        vat_amount: trustedTotals.vat,
        gross_amount: trustedTotals.gross,
        confidence: sanitizedResult?.confidence || 0.8,
        // Always require human review on first parse
        status: 'needs_review',
        vendor_id: matchedVendor?.id || null,
        vendor_account_ref: matchedVendor?.account_ref || null,
      };

      // Save parsed invoice to database
      const { data: invoiceData, error: invoiceError } = await supabase
        ?.from('parsed_invoices')
        ?.insert(invoicePayload)
        ?.select()
        ?.single();

      if (invoiceError) throw invoiceError;

      // Save raw extracted data as an envelope so Debug modal can show
      // - parsed_json: immutable snapshot of the model's first parse
      // - current: working copy the editor can mutate
      // - original_text: reserved for verbatim model text (not available here)
      // - meta: provenance info
      const envelope = {
        meta: {
          model: modelToUse,
          created_at: new Date().toISOString(),
          vendor_matched: !!matchedVendor,
        },
        original_text: null,
        parsed_json: sanitizedResult || {},
        current: sanitizedResult || {},
        // Back-compat: expose line_items at root for consumers that expect data.line_items
        line_items: (sanitizedResult?.line_items || []).map(li => ({
          description: li?.description || '',
          quantity: typeof li?.quantity === 'number' ? li?.quantity : parseFloat(li?.quantity || 0) || 0,
          unit_price: typeof li?.unit_price === 'number' ? li?.unit_price : parseFloat(li?.unit_price || 0) || 0,
          net_amount: typeof li?.net_amount === 'number' ? li?.net_amount : parseFloat(li?.net_amount || 0) || 0,
          vat_rate: typeof li?.vat_rate === 'number' ? li?.vat_rate : parseFloat(li?.vat_rate || 0) || 0,
          vat_amount: typeof li?.vat_amount === 'number' ? li?.vat_amount : parseFloat(li?.vat_amount || 0) || 0,
          vat_code: li?.vat_code || null,
          total: typeof li?.total === 'number' ? li?.total : parseFloat(li?.total || 0) || 0,
        })),
      };

      const { error: rawDataError } = await supabase
        ?.from('raw_extracted_data')
        ?.insert({
          invoice_id: invoiceData?.id,
          data: envelope,
        });

      if (rawDataError) throw rawDataError;

      // Create audit log entry
      await this.createAuditLog('parsed_invoices', invoiceData?.id, 'created', userId, orgId, {
        source: 'AI_parsing',
        confidence: sanitizedResult?.confidence,
        vendor_matched: !!matchedVendor,
      });

      return invoiceData;
    } catch (error) {
      console.error('Error parsing invoice:', error);
      throw error;
    }
  }

  /**
   * Get user's invoices with filtering and pagination
   * @param {string} userId - User ID
   * @param {Object} filters - Filter options
   * @param {number} page - Page number (0-based)
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Paginated invoice list
   */
  static async getUserInvoices(orgId, filters = {}, page = 0, limit = 20) {
    try {
      let query = supabase?.from('parsed_invoices')?.select(`
          *,
          documents!inner(
            file_name,
            uploaded_at,
            storage_path
          )
        `)?.eq('org_id', orgId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.vendor_name) {
        query = query?.ilike('vendor_name', `%${filters?.vendor_name}%`);
      }
      if (filters?.date_from) {
        query = query?.gte('invoice_date', filters?.date_from);
      }
      if (filters?.date_to) {
        query = query?.lte('invoice_date', filters?.date_to);
      }

      // Apply pagination
      const start = page * limit;
      const end = start + limit - 1;
      query = query?.range(start, end);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        data: data || [],
        count: count || 0,
        page,
        limit,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      console.error('Error fetching user invoices:', error);
      throw error;
    }
  }

  /**
   * Update invoice data
   * @param {string} invoiceId - Invoice ID
   * @param {string} userId - User ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated invoice data
   */
  static async updateInvoice(invoiceId, userId, orgId, updates) {
    try {
      const { data, error } = await supabase?.from('parsed_invoices')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', invoiceId)?.eq('org_id', orgId)?.select()?.single();

      if (error) throw error;

      // Create audit log entry
      await this.createAuditLog('parsed_invoices', invoiceId, 'updated', userId, orgId, {
        changes: updates
      });

      return data;
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  }

  /**
   * Delete invoice and associated data
   * @param {string} invoiceId - Invoice ID
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  static async deleteInvoice(invoiceId, userId, orgId) {
    try {
      // First delete raw extracted data (due to foreign key)
      const { error: rawDataError } = await supabase?.from('raw_extracted_data')?.delete()?.eq('invoice_id', invoiceId);

      if (rawDataError) throw rawDataError;

      // Delete invoice
      const { error: invoiceError } = await supabase?.from('parsed_invoices')?.delete()?.eq('id', invoiceId)?.eq('org_id', orgId);

      if (invoiceError) throw invoiceError;

      // Create audit log entry
      await this.createAuditLog('parsed_invoices', invoiceId, 'deleted', userId, orgId);

    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  }

  /**
   * Get vendors for dropdown/selection
   * @returns {Promise<Array>} List of vendors
   */
  static async getVendors(orgId) {
    try {
      const { data, error } = await supabase?.from('vendors')?.select('*')?.eq('active', true)?.eq('org_id', orgId)?.order('display_name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  }

  /**
   * Match vendor name to a known vendor
   * @param {string} vendorName - Extracted vendor name
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object|null>} Matching vendor or null
   */
  static async matchVendor(vendorName, orgId) {
    try {
      if (!vendorName) return null;

      const { data: vendors } = await supabase
        .from('vendors')
        .select('id, display_name, legal_name, account_ref')
        .eq('active', true)
        .eq('org_id', orgId);

      if (!vendors || vendors.length === 0) {
        return null;
      }

      const normalize = (str) =>
        (str || '')
          .toLowerCase()
          .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
          
      const normalizedVendorName = normalize(vendorName);

      if (!normalizedVendorName) {
        return null;
      }

      // 1. Exact match on normalized names
      for (const vendor of vendors) {
        const namesToCheck = [vendor.display_name, vendor.legal_name].filter(Boolean);
        for (const name of namesToCheck) {
          if (normalize(name) === normalizedVendorName) {
            return vendor;
          }
        }
      }

      // 2. Word-based subset matching for more nuance
      const vendorWords = new Set(normalizedVendorName.split(' '));
      for (const vendor of vendors) {
        const namesToCheck = [vendor.display_name, vendor.legal_name].filter(Boolean);
        for (const name of namesToCheck) {
          const normalizedVendorNameToMatch = normalize(name);
          if (!normalizedVendorNameToMatch) continue;

          const vendorWordsToMatch = new Set(normalizedVendorNameToMatch.split(' '));

          const [smallerSet, largerSet] =
            vendorWords.size < vendorWordsToMatch.size
              ? [vendorWords, vendorWordsToMatch]
              : [vendorWordsToMatch, vendorWords];

          let allWordsFound = true;
          for (const word of smallerSet) {
            if (!largerSet.has(word)) {
              allWordsFound = false;
              break;
            }
          }

          if (allWordsFound) {
            return vendor;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error matching vendor:', error);
      return null;
    }
  }

  /**
   * Create audit log entry
   * @param {string} entityType - Type of entity
   * @param {string} entityId - Entity ID
   * @param {string} entityId - Entity ID
   * @param {string} action - Action performed
   * @param {string} userId - User ID
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<void>}
   */
  static async createAuditLog(entityType, entityId, action, userId, orgId, metadata = {}) {
    try {
      const { error } = await supabase?.from('audit_log')?.insert({
          entity_type: entityType,
          entity_id: entityId,
          action,
          user_id: userId,
          org_id: orgId,
          meta_json: metadata
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error creating audit log:', error);
      // Don't throw here as audit logging is not critical
    }
  }

  /**
   * Get invoice statistics for dashboard
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Statistics object
   */
  static async getInvoiceStats(orgId) {
    try {
      // Get total counts by status
      const { data: statusCounts, error: statusError } = await supabase?.from('parsed_invoices')?.select('status')?.eq('org_id', orgId);

      if (statusError) throw statusError;

      // Get total amounts
      const { data: amountData, error: amountError } = await supabase?.from('parsed_invoices')?.select('gross_amount, currency')?.eq('org_id', orgId)?.not('gross_amount', 'is', null);

      if (amountError) throw amountError;

      // Calculate statistics
      const stats = {
        total: statusCounts?.length || 0,
        parsed: statusCounts?.filter(i => i?.status === 'parsed')?.length || 0,
        needs_review: statusCounts?.filter(i => i?.status === 'needs_review')?.length || 0,
        reviewed: statusCounts?.filter(i => i?.status === 'reviewed')?.length || 0,
        exported: statusCounts?.filter(i => i?.status === 'exported')?.length || 0,
        total_amount: amountData?.reduce((sum, item) => sum + (parseFloat(item?.gross_amount) || 0), 0) || 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching invoice stats:', error);
      throw error;
    }
  }
}

export default InvoiceService;

// Helpers
async function computeFileSha256(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hex;
}
